<template>

    <div class="ui-dropdown" tabindex="0"
         :class="{ disabled, placeholder: titleIsPlaceholder, error: hasError, focussed: activated }"
         @focus="onFocus" @blur="onDropdownBlur">

        <div class="value" @click="onClick">

            <template v-if="numberInputVisible">

                <ui-input number v-model="numberValue"
                          :focus="activated" ref="numberInputRef"
                          @update:modelValue="onNumberValueInput" @change="onNumberValueChange" @blur="onInputBlur" />

            </template>
            <template v-else>

                <slot name="current" :title="title" :option="selectedOption">
                    <div class="current">
                        <img v-if="selectedOption && selectedOption.image" :src="selectedOption.image" :alt="selectedOption.label"/>
                        <span>{{ title }}</span>
                    </div>
                </slot>

                <ui-input
                    v-if="inputVisible" ref="searchInputRef"
                    :placeholder="searchPlaceholder || $t('ui.controls.dropdown.search')"
                    v-model="searchQuery"
                    @blur="onInputBlur" focus />

            </template>

            <i class="icon bx bx-chevron-down" ref="chevron"></i>

        </div>

    </div>

</template>

<script lang="ts">

import 'boxicons';

import { Component, Prop, Ref, Vue, Watch } from 'vue-facing-decorator';

import { debounce, find, isArray, isFunction, isString } from 'lodash';

import type { CollectionOption, CollectionOptionsArgument, ComponentProps } from '@wittignl/js-core';
import { createComponentMixin, resolveOptions, uuid } from '@wittignl/js-core';

import type { PopupOptions } from '../overlay';
import popupMixin from '../../mixin/popup';

import type { InputComp } from './Input.vue';
import Input from './Input.vue';

import OptionPickerPopup from '../popup/OptionPickerPopup.vue';

const DROPDOWN_SEARCH_OPTION = '$DROPDOWN_SEARCH_OPTION'

type AsyncFetcher = (query: string) => Promise<CollectionOptionsArgument>

@Component({
    name: 'ui-dropdown',
    mixins: [popupMixin],
    components: {
        'ui-input': Input
    },
    emits: ['update:modelValue', 'change', 'search']
})
export default class Dropdown extends Vue {

    @Prop({ type: String })
    placeholder: string;

    @Prop({ type: String })
    searchPlaceholder: string;

    @Prop({ type: [String, Number, Boolean, Object, Array] })
    modelValue: string | number | boolean | object | any[];

    @Prop({ type: [Array, Object, Function] })
    options: CollectionOptionsArgument | AsyncFetcher;

    @Prop({ type: [Boolean, String, Array] })
    error: boolean | string | string[];

    @Prop({ type: [Boolean, String] })
    empty: boolean | string;

    @Prop({ type: Boolean })
    multi: boolean;

    @Prop({ type: Boolean })
    disabled: boolean;

    @Prop({ type: Boolean })
    searchable: boolean;

    @Prop({ type: Boolean })
    number: boolean;

    @Prop({ type: Boolean })
    async: boolean;

    @Prop({ type: Boolean })
    searchOption: boolean;

    @Prop({ type: Boolean })
    popupAutoWidth: boolean;

    @Ref
    searchInputRef: InputComp;

    @Ref
    numberInputRef: InputComp;

    activated = false;
    popupFullVisible = false;

    optionPickerId: string = null;
    optionPickerOptions: CollectionOption[] = null;

    searchQuery: string = null;
    numberValue: number = null;

    debouncedAsyncFetch: (string) => Promise<any>

    created() {

        this.debouncedAsyncFetch = debounce(this.asyncFetch.bind(this), 200);
        this.$watch('searchQuery', this.debouncedAsyncFetch);
        this.$watch('numberValue', this.debouncedAsyncFetch);

        this.updateNumberFromValue();
    }

    @Watch('modelValue')
    onModelValueUpdate() {

        this.updateNumberFromValue();
    }

    get resolvedOptions() {

        let options = !isFunction(this.options) ? resolveOptions(this.options as CollectionOptionsArgument) : [];

        return this.decorateOptions(options);
    }

    get emptyText() {

        if(!this.empty){
            return null;
        }

        return isString(this.empty) ? this.empty : this.$t('ui.controls.dropdown.empty');
    }

    get title() {

        const option = this.selectedOption;

        if(this.multi){

            if(!option.length){
                return this.emptyText || this.placeholder;
            }

            const selectedOptions = this.resolvedOptions.filter(o => option.includes(o.value));

            return selectedOptions.length > 1 ? `${this.$n(option.length)} ${this.$t('ui.controls.dropdown.multi-value')}` : selectedOptions[0].label;
        }
        else {

            if(!option){
                return this.emptyText || this.placeholder;
            }

            return option.label;
        }
    }

    get titleIsPlaceholder() {

        if(this.multi){
            return !this.selectedOption.length && !this.emptyText;
        }
        else {
            return !this.selectedOption && !this.emptyText;
        }
    }

    get selectedOption(): any|any[] {

        if(this.modelValue === null || this.modelValue === undefined){
            return this.multi ? [] : null;
        }

        if(this.async || this.multi || this.number){
            return this.modelValue;
        }

        return find(this.resolvedOptions, { value: this.modelValue }) || null;
    }

    get hasError() {

        return isArray(this.error) ? this.error.length : !!this.error;
    }

    get inputVisible() {

        if(this.async){
            return this.activated;
        }
        else {
            return this.searchable && this.activated;
        }
    }

    get numberInputVisible() {

        return this.number && (this.numberValue !== null || this.activated);
    }

    decorateOptions(inputOptions: CollectionOption[]): CollectionOption[] {

        let options = [...inputOptions];

        if(this.searchQuery){

            options = options.filter(
                option => option.label.toLowerCase().indexOf(this.searchQuery.toLowerCase()) !== -1
            );
        }

        if(this.searchQuery && this.searchOption){

            const search = this.searchQuery.toLowerCase();

            const existingOption = options.find(opt => opt.label.toLowerCase() === search);

            if(!existingOption){

                options.unshift({
                    label: this.searchQuery,
                    value: DROPDOWN_SEARCH_OPTION
                });
            }
        }

        if(this.empty && options.length > 0){

            options.unshift({
                label: this.emptyText,
                value: null
            });
        }

        return options;
    }

    onClick() {

        this.activateDropdown();
    }

    onFocus() {

        this.activateDropdown();
    }

    onDropdownBlur(e) {

        if(!this.popupFullVisible || !e.relatedTarget || this.elementIsInPicker(e.relatedTarget)){
            e.stopImmediatePropagation();
            e.preventDefault();
            return false;
        }

        if(!this.inputVisible && !this.numberInputVisible){
            this.deactivateDropdown();
        }
    }

    onInputBlur(e) {

        if(!this.popupFullVisible || !e.relatedTarget || this.elementIsInPicker(e.relatedTarget)){
            e.stopImmediatePropagation();
            e.preventDefault();
            return false;
        }

        this.deactivateDropdown();
    }

    onNumberValueInput(value) {

        if(value !== this.modelValue){
            this.updateValue({ value }, true);
        }
    }

    onNumberValueChange() {

        this.$emit('change', this.numberValue);
    }

    activateDropdown() {

        if(this.disabled || this.optionPickerId){
            return;
        }

        this.searchQuery = null;

        if(this.async){
            this.activated = true;
            this.asyncFetch(null);
        }
        else {
            this.showPicker(() => this.resolvedOptions);
        }
    }

    deactivateDropdown() {

        this.activated = false;

        if(this.optionPickerId){

            this.$overlay.close(this.optionPickerId);
            this.optionPickerId = null;
        }
    }

    updateNumberFromValue() {

        if(this.number){
            this.numberValue = this.modelValue as number;
        }
    }

    async showPicker(optionsProvider) {

        this.activated = true;
        this.optionPickerId = `ui-dropdown-option-picker-${uuid()}`;

        this.popupFullVisible = false;

        this.$nextTick(async () => {

            const rect = this.$el.getBoundingClientRect();

            const tooltipOptions: PopupOptions = {
                anchor: this.$el,
                placement: 'bottom|top|left|right-center|left|right'
            };

            const props = () => {

                let value = this.selectedOption;
                if(!this.multi && !this.number && value){
                    value = value.value;
                }

                return {
                    width: !this.popupAutoWidth ? rect.width : null,
                    options: optionsProvider(),
                    modelValue: value,
                    multi: this.multi
                };
            };

            this.$nextTick(() => {
                this.popupFullVisible = true;
            });

            const { result } = await this.$popup(OptionPickerPopup, tooltipOptions, props, {

                id: this.optionPickerId,
                blocking: false,
                slots: {
                    option: this.$slots.option
                },
                beforeCancel: event => {

                    const $search = this.searchInputRef?.$el;
                    if(!$search){
                        return true;
                    }

                    return event.target !== $search && !$search.contains(event.target);
                },
                on: {
                    change: value => {
                        this.updateValue(value);
                    }
                }
            });

            if(result){

                this.updateValue(result);

                if(this.number){
                    this.numberValue = result.value;
                }
            }

            this.optionPickerId = null;
            this.activated = false;
        });
    }

    async asyncFetch(query: string) {

        if(!this.activated || !this.async){
            return;
        }

        if(!isFunction(this.options)){
            throw 'Options should be function on async mode';
        }

        const options = await this.options(query);
        if(!options){
            return;
        }

        this.optionPickerOptions = resolveOptions(options);

        if(!this.optionPickerId){
            this.showPicker(() => this.optionPickerOptions);
        }
    }

    updateValue(option: CollectionOption, skipChange=false) {

        if(this.searchOption && option.value === DROPDOWN_SEARCH_OPTION){

            this.$emit('search', option.label);
            return;
        }

        const value = this.async || this.multi ? option : option.value;

        this.$emit('update:modelValue', value);

        if(!skipChange){

            this.$nextTick(() => {
                this.$emit('change', value);
            });
        }
    }

    elementIsInPicker(element) {

        const $picker = this.$overlay.findOverlay(this.optionPickerId)?.$overlayRef().$el;
        return $picker && element && ($picker === element || $picker.contains(element));
    }

    beforeUnmount() {

        this.deactivateDropdown();
    }
}

export type DropdownComp = InstanceType<typeof Dropdown>
export type DropdownProps = ComponentProps<DropdownComp>
export const dropdownCompMixin = createComponentMixin(Dropdown)

</script>
<style src="../../../scss/component/control/Dropdown.scss" lang="scss"></style>
